import * as React from 'react'
import type { SupportedLanguage } from '../../../shared/i18n/i18n.no-deps'
import type { KnownUnit } from '../unit/unit'
import type { CommonDeps } from '../base/CommonDeps'
import { renderCatch } from '../error/renderCatch'
import type { RenderCachedImage } from '../image/WithCachedImage'
import type { BarcodeResult } from '../unit/barcodeTypes'
import { usePromiseManager } from '../util/PromiseManager'
import { useAsyncEffect } from '../util/react'
import { Maybe, Result } from '@zaptic-external/ts-monads'
import _ from 'lodash'
import { getSearchTree } from '../unit/helpers'
import {
    findTreeNodeWithIterationPath,
    type Tree,
    type TreeNode,
    treeValues,
} from '../../../shared/lib/typeHelpers.no-deps'
import classNames from 'classnames'
import type { Renderable } from '../ui/SearchableDropdown'
import { useFocus } from '../util/focus'
import { v4 as uuidv4 } from 'uuid'
import i18next from 'i18next'
import { useInfiniteLoading } from '../util/infiniteLoading'
import { UnitQRScanButton } from '../unit/Unit.qr.scanner'

interface Props {
    deps: CommonDeps
    startLabel: string
    onStart: () => Promise<void> | void
    maybeSelectedUnitId: Maybe<number>
    unitTypeId: number
    onSelect: (selectedUnitId: number) => void
    fetchUnitTypeName: (unitTypeId: number) => Promise<Maybe<string>>
    units: KnownUnit[]
    ancestors: KnownUnit[]
    renderCachedImage: RenderCachedImage
    barcodeScanForUnits?: (units: KnownUnit[]) => Promise<BarcodeResult>
}

type UnitsPicker = {
    unitsTree: UnitsTree
    mSelectedUnitId: Maybe<number>
    mSelectedNodeId: Maybe<string>
    searchPhrase: string
    expandedCategories: Map<number, boolean>
    /* Fields below stores intermediate values for performance reasons.
       We store them in the state so we don't have to compute them on every render, which is slow.
       Disadvantage of this approach is the need to keep these value in sync with the source value.
    */
    unitsListForSearch: UnitForSearch[]
    foundUnitsBySearch: UnitForSearch[]
}

type UnitForSearch = { id: number; name: string; unitOption: UnitOption }

type UnitsTree = Tree<UnitOption | UnitCategory>

type UnitOption = {
    type: 'option'
    id: number
    name: string
    image: React.ReactNode
    nodeId: string
}

type UnitCategory = {
    type: 'category'
    id: number
    name: string
    unitsCount: number
    nodeId: string
}

export function RedesignedUnitSubjectPicker(props: Props) {
    const language = props.deps.language

    const [state, setState] = React.useState<UnitsPicker>({
        unitsTree: getSearchTree(props.deps, props.renderCachedImage, props.units, props.ancestors).map(
            mapNodeToUnitNode
        ),
        expandedCategories: new Map(),
        mSelectedUnitId: props.maybeSelectedUnitId,
        mSelectedNodeId: Maybe.nothing(),
        searchPhrase: '',
        unitsListForSearch: [],
        foundUnitsBySearch: [],
    })
    const unitTypeNamePM = usePromiseManager<Maybe<string>>(props.deps)
    const [inputRef, setInputFocus] = useFocus()

    // Runs effect before render
    React.useLayoutEffect(() => {
        const unitsListForSearch = treeValues(state.unitsTree)
            .filter((data) => isUnitOption(data))
            .map((unitOption) => ({
                id: unitOption.id,
                name: unitOption.name.toLowerCase(),
                unitOption: unitOption as UnitOption,
            }))

        setState({ ...state, unitsListForSearch, foundUnitsBySearch: unitsListForSearch })
    }, [])

    const itemsNumberToLoadPerPage = 100

    // use infinite loading only on first level of units tree
    const { items: loadedItems, setElementTriggeringLoadWhenVisible } = useInfiniteLoading({
        initialPageNumber: 1,
        getItems: async (pageNumber) => {
            return Result.ok({
                items: state.unitsTree
                    .slice((pageNumber - 1) * itemsNumberToLoadPerPage, pageNumber * itemsNumberToLoadPerPage)
                    .map((treeNode) => ({ id: treeNode.data.id, node: treeNode })),
                totalPagesCount: Math.ceil(state.unitsTree.length / itemsNumberToLoadPerPage),
            })
        },
    })

    useAsyncEffect(async () => {
        unitTypeNamePM.trigger(() => props.fetchUnitTypeName(props.unitTypeId))
    }, [])

    const handleQrScan = (unitId: number) => {
        setState(selectUnit(unitId, state))
        props.onSelect(unitId)
    }

    const render = (unitTypeName: string) => {
        const [mSelectedUnit, ancestorCategories] = state.mSelectedUnitId
            .map((selectedUnitId) =>
                findUnitWithAncestorCategories(
                    (data) =>
                        (state.mSelectedNodeId.isJust() && state.mSelectedNodeId.value === data.nodeId) ||
                        (state.mSelectedNodeId.isNothing() && selectedUnitId === data.id),
                    state.unitsTree
                )
            )
            .withDefault([Maybe.nothing(), []])
        const foundUnits: UnitOption[] =
            state.searchPhrase === '' ? [] : state.foundUnitsBySearch.map((data) => data.unitOption)
        return (
            <div className="AppCore__report__RedesignedUnitSubjectPicker">
                <div className="AppCore__report__RedesignedUnitSubjectPicker__mainContent">
                    <div className="AppCore__report__RedesignedUnitSubjectPicker__selectLabel">
                        {selectUnitTranslatedText(unitTypeName, language)}
                    </div>
                    <div className="AppCore__report__RedesignedUnitSubjectPicker__selectInput">
                        <div
                            className="AppCore__report__RedesignedUnitSubjectPicker__searchBox"
                            onClick={() => setInputFocus()}
                        >
                            <div className="AppCore__report__RedesignedUnitSubjectPicker__searchBox__icon">
                                <zaptic-icon icon-name="magnifier"></zaptic-icon>
                            </div>
                            <input
                                type="text"
                                value={state.searchPhrase}
                                placeholder={searchPlaceholderText(language)}
                                className="AppCore__report__RedesignedUnitSubjectPicker__searchBox__input"
                                onChange={(ev) => setState(searchForUnit(ev.target.value, state))}
                                ref={inputRef}
                            />
                            {state.searchPhrase !== '' ? (
                                <div
                                    className="AppCore__report__RedesignedUnitSubjectPicker__searchBox__clearButton"
                                    onClick={() => setState(clearSearch(state))}
                                >
                                    <zaptic-icon icon-name="delete-cross-in-circle"></zaptic-icon>
                                </div>
                            ) : null}
                        </div>
                        <UnitQRScanButton
                            deps={props.deps}
                            unitsToFindResultWithin={props.units}
                            onSelect={handleQrScan}
                            doScan={() => props.barcodeScanForUnits(props.units)}
                        />
                    </div>
                    {mSelectedUnit.isNothing() ? null : (
                        <div className="AppCore__report__RedesignedUnitSubjectPicker__currentSelection">
                            <div className="AppCore__report__RedesignedUnitSubjectPicker__currentSelection__label">
                                {currentSelectionText(language)}:
                            </div>
                            <div className="AppCore__report__RedesignedUnitSubjectPicker__currentSelection__content">
                                <UnitWithCategories
                                    image={mSelectedUnit.value.image}
                                    name={mSelectedUnit.value.name}
                                    categoriesBreadcrumbs={ancestorCategories.map((category) => category.name)}
                                />
                                <div
                                    className="AppCore__report__RedesignedUnitSubjectPicker__currentSelection__clearButton"
                                    onClick={() => setState(clearSelection(state))}
                                >
                                    <zaptic-icon icon-name="delete-cross-in-circle"></zaptic-icon>
                                </div>
                            </div>
                        </div>
                    )}
                    {props.units.length === 0 ? (
                        <Warning text={noUnitsText(language)}></Warning>
                    ) : state.searchPhrase === '' ? (
                        <div className="AppCore__report__RedesignedUnitSubjectPicker__unitsTree">
                            <Units
                                unitsTree={loadedItems.map((item) => item.node)}
                                expandedCategories={state.expandedCategories}
                                mSelectedUnitId={state.mSelectedUnitId}
                                onSelect={(unitId: number, nodeId?: string) => {
                                    setState(selectUnit(unitId, state, nodeId))
                                    props.onSelect(unitId)
                                }}
                                depthLevel={0}
                                onCategoryButtonClick={(categoryUnitId: number) =>
                                    setState(toggleCategory(categoryUnitId, state))
                                }
                                setElementTriggeringLoadWhenVisible={setElementTriggeringLoadWhenVisible}
                            />
                        </div>
                    ) : foundUnits.length === 0 ? (
                        <Warning text={noSearchResultsText(language)}></Warning>
                    ) : (
                        <SearchResults
                            unitsTree={state.unitsTree}
                            foundUnits={foundUnits}
                            maybeSelectedUnitId={state.mSelectedUnitId}
                            onUnitOptionSelect={(unitOption: UnitOption) => {
                                setState(selectUnit(unitOption.id, state, unitOption.nodeId))
                                props.onSelect(unitOption.id)
                            }}
                        />
                    )}
                </div>
                <div className="AppCore__report__RedesignedUnitSubjectPicker__startButton">
                    <zaptic-button
                        onClick={() => {
                            if (state.mSelectedUnitId.isJust()) props.onStart()
                        }}
                        size="large"
                        disabled={state.mSelectedUnitId.isNothing()}
                    >
                        {props.startLabel}
                    </zaptic-button>
                </div>
            </div>
        )
    }

    return renderCatch(props.deps, () => {
        if (unitTypeNamePM.getState().status === 'rejected') {
            return render(unitNameFallback(language))
        } else {
            return unitTypeNamePM.render((mUnitTypeName) =>
                mUnitTypeName
                    .map((unitTypeName) => render(unitTypeName))
                    .withDefault(render(unitNameFallback(language)))
            )
        }
    })
}

const isUnitOption = (unit: UnitOption | UnitCategory): unit is UnitOption => {
    return unit.type === 'option'
}

const toggleCategory = (categoryUnitId: number, unitsPicker: UnitsPicker): UnitsPicker => {
    return {
        ...unitsPicker,
        expandedCategories: new Map(unitsPicker.expandedCategories).set(
            categoryUnitId,
            !unitsPicker.expandedCategories.get(categoryUnitId)
        ),
    }
}

const selectUnit = (unitId: number, unitsPicker: UnitsPicker, nodeId?: string): UnitsPicker => {
    return {
        ...unitsPicker,
        mSelectedUnitId: Maybe.just(unitId),
        mSelectedNodeId: Maybe.fromOptional(nodeId),
    }
}

const clearSelection = (unitsPicker: UnitsPicker): UnitsPicker => {
    return {
        ...unitsPicker,
        mSelectedUnitId: Maybe.nothing(),
        mSelectedNodeId: Maybe.nothing(),
    }
}

const clearSearch = (unitsPicker: UnitsPicker): UnitsPicker => {
    return {
        ...unitsPicker,
        searchPhrase: '',
        foundUnitsBySearch: unitsPicker.unitsListForSearch,
    }
}

const searchForUnit = (phrase: string, unitsPicker: UnitsPicker): UnitsPicker => {
    return {
        ...unitsPicker,
        searchPhrase: phrase,
        foundUnitsBySearch: searchForMaxNUnits(phrase, unitsPicker.unitsListForSearch, 50),
    }
}

const searchForMaxNUnits = (phrase: string, unitsForSearch: UnitForSearch[], max: number): UnitForSearch[] => {
    const foundUnits: UnitForSearch[] = []
    for (const unitForSearch of unitsForSearch) {
        if (foundUnits.length === max) {
            return foundUnits
        }
        if (unitForSearch.name.includes(phrase.toLowerCase())) {
            foundUnits.push(unitForSearch)
        }
    }
    return foundUnits
}

const mapNodeToUnitNode = (node: TreeNode<Renderable<number>>): TreeNode<UnitOption | UnitCategory> => {
    return node.children.length === 0
        ? {
              data: {
                  type: 'option',
                  id: node.data.id,
                  name: node.data.label,
                  image: node.data.leftNode,
                  nodeId: uuidv4(),
              },
              children: [],
          }
        : {
              data: {
                  type: 'category',
                  id: node.data.id,
                  name: node.data.label,
                  unitsCount: node.children.length,
                  nodeId: uuidv4(),
              },
              children: node.children.map(mapNodeToUnitNode),
          }
}

const findUnitWithAncestorCategories = (
    nodeMatch: (data: UnitOption | UnitCategory) => boolean,
    unitsTree: UnitsTree
): [Maybe<UnitOption>, UnitCategory[]] => {
    const [node, ancestorNodes] = findTreeNodeWithIterationPath(
        (data) => data.type === 'option' && nodeMatch(data),
        unitsTree,
        []
    )
    return [
        Maybe.fromOptional(node).map((node) => node.data as UnitOption),
        ancestorNodes.map((node) => node.data as UnitCategory),
    ]
}

const UnitWithCategories = (props: { image: React.ReactNode; name: string; categoriesBreadcrumbs: string[] }) => {
    return (
        <div className="AppCore__report__RedesignedUnitSubjectPicker__currentSelection__unit">
            <div className="AppCore__report__RedesignedUnitSubjectPicker__currentSelection__unit__image">
                {props.image}
            </div>
            <div className="AppCore__report__RedesignedUnitSubjectPicker__currentSelection__unit__description">
                <div className="AppCore__report__RedesignedUnitSubjectPicker__currentSelection__unit__name">
                    {props.name}
                </div>
                <div className="AppCore__report__RedesignedUnitSubjectPicker__currentSelection__unit__ancestors">
                    {props.categoriesBreadcrumbs.join(' > ')}
                </div>
            </div>
        </div>
    )
}

const Units = (props: {
    unitsTree: UnitsTree
    expandedCategories: Map<number, boolean>
    mSelectedUnitId: Maybe<number>
    onSelect: (unitId: number, nodeId?: string) => void
    depthLevel: number
    onCategoryButtonClick: (categoryUnitId: number) => void
    setElementTriggeringLoadWhenVisible: React.Dispatch<React.SetStateAction<Element>>
}) => {
    return (
        <div>
            {props.unitsTree.map((node, index) => (
                <div
                    key={node.data.id}
                    ref={
                        isLastTopLevelUnit(props.depthLevel, props.unitsTree.length, index)
                            ? props.setElementTriggeringLoadWhenVisible
                            : null
                    }
                >
                    {node.data.type === 'option' ? (
                        <UnitOption
                            unitOption={node.data}
                            onSelect={props.onSelect}
                            isSelected={props.mSelectedUnitId
                                .map((unitId) => node.data.id === unitId)
                                .withDefault(false)}
                        />
                    ) : (
                        <UnitCategory
                            unitCategory={node.data}
                            categoryUnits={node.children}
                            expandedCategories={props.expandedCategories}
                            mSelectedUnitId={props.mSelectedUnitId}
                            onUnitSelect={props.onSelect}
                            depthLevel={props.depthLevel}
                            onCategoryButtonClick={props.onCategoryButtonClick}
                            setElementTriggeringLoadWhenVisible={props.setElementTriggeringLoadWhenVisible}
                        />
                    )}
                </div>
            ))}
        </div>
    )
}

const isLastTopLevelUnit = (depthLevel: number, unitsCount: number, unitIndex: number): boolean =>
    depthLevel === 0 && unitsCount - 1 === unitIndex

const UnitOption = (props: {
    unitOption: UnitOption
    isSelected: boolean
    onSelect: (unitId: number, nodeId?: string) => void
}) => {
    return (
        <div
            className={classNames('AppCore__report__RedesignedUnitSubjectPicker__option', {
                selected: props.isSelected,
            })}
            onClick={() => props.onSelect(props.unitOption.id, props.unitOption.nodeId)}
        >
            <OptionCheckbox isSelected={props.isSelected} />
            <div className="AppCore__report__RedesignedUnitSubjectPicker__option__image">{props.unitOption.image}</div>
            <div className="AppCore__report__RedesignedUnitSubjectPicker__option__label">{props.unitOption.name}</div>
        </div>
    )
}

const OptionCheckbox = (props: { isSelected: boolean }) => {
    return (
        <div
            className={classNames('AppCore__report__RedesignedUnitSubjectPicker__checkbox', {
                selected: props.isSelected,
            })}
        >
            {props.isSelected ? (
                <div className="AppCore__report__RedesignedUnitSubjectPicker__checkboxSelected" />
            ) : null}
        </div>
    )
}

const UnitCategory = (props: {
    unitCategory: UnitCategory
    expandedCategories: Map<number, boolean>
    categoryUnits: UnitsTree
    mSelectedUnitId: Maybe<number>
    onUnitSelect: (unitId: number) => void
    depthLevel: number
    onCategoryButtonClick: (categoryUnitId: number) => void
    setElementTriggeringLoadWhenVisible: React.Dispatch<React.SetStateAction<Element>>
}) => {
    return (
        <div className={`AppCore__report__RedesignedUnitSubjectPicker__category`}>
            <div
                className="AppCore__report__RedesignedUnitSubjectPicker__category__button"
                onClick={() => props.onCategoryButtonClick(props.unitCategory.id)}
            >
                <div
                    className={classNames('AppCore__report__RedesignedUnitSubjectPicker__category__button__chevron', {
                        expanded: props.expandedCategories.get(props.unitCategory.id),
                    })}
                >
                    <zaptic-icon icon-name="arrow-right"></zaptic-icon>
                </div>
                <div className="AppCore__report__RedesignedUnitSubjectPicker__category__button__label">
                    <span className="AppCore__report__RedesignedUnitSubjectPicker__category__button__name">
                        {props.unitCategory.name}
                    </span>
                    <span className="AppCore__report__RedesignedUnitSubjectPicker__category__button__allOptionsCount">
                        ({props.unitCategory.unitsCount})
                    </span>
                </div>
            </div>
            {!props.expandedCategories.get(props.unitCategory.id) ? null : (
                <div className="AppCore__report__RedesignedUnitSubjectPicker__category__units">
                    {
                        <Units
                            unitsTree={props.categoryUnits}
                            expandedCategories={props.expandedCategories}
                            mSelectedUnitId={props.mSelectedUnitId}
                            onSelect={props.onUnitSelect}
                            depthLevel={props.depthLevel + 1}
                            onCategoryButtonClick={props.onCategoryButtonClick}
                            setElementTriggeringLoadWhenVisible={props.setElementTriggeringLoadWhenVisible}
                        />
                    }
                </div>
            )}
        </div>
    )
}

const Warning = (props: { text: string }) => {
    return (
        <div className="AppCore__report__RedesignedUnitSubjectPicker__warning">
            <div>{props.text}</div>
        </div>
    )
}

const SearchResults = (props: {
    foundUnits: UnitOption[]
    unitsTree: UnitsTree
    onUnitOptionSelect: (unitOption: UnitOption) => void
    maybeSelectedUnitId: Maybe<number>
}) => {
    return (
        <div className="AppCore__report__RedesignedUnitSubjectPicker__searchResults">
            {props.foundUnits.map((unitOption) => {
                const [, categories] = findUnitWithAncestorCategories(
                    (data) => unitOption.nodeId === data.nodeId,
                    props.unitsTree
                )
                const categoriesBreadcrumbs = categories.map((category) => category.name)
                return (
                    <div
                        className="AppCore__report__RedesignedUnitSubjectPicker__searchResults__option"
                        key={unitOption.id}
                        onClick={() => props.onUnitOptionSelect(unitOption)}
                    >
                        <OptionCheckbox
                            isSelected={props.maybeSelectedUnitId
                                .map((unitId) => unitOption.id === unitId)
                                .withDefault(false)}
                        />
                        <UnitWithCategories
                            image={unitOption.image}
                            name={unitOption.name}
                            categoriesBreadcrumbs={categoriesBreadcrumbs}
                        />
                    </div>
                )
            })}
        </div>
    )
}

const selectUnitTranslatedText = (unitTypeName: string, _: SupportedLanguage): string =>
    i18next.t('appcore:redesignedSubjectPicker.selectUnitTranslatedText', { unitTypeName })

const unitNameFallback = (_: SupportedLanguage) => i18next.t('appcore:redesignedSubjectPicker.unitNameFallback')

const currentSelectionText = (_: SupportedLanguage) => i18next.t('appcore:redesignedSubjectPicker.currentSelectionText')

const searchPlaceholderText = (_: SupportedLanguage) =>
    i18next.t('appcore:redesignedSubjectPicker.searchPlaceholderText')

const noUnitsText = (_: SupportedLanguage) => i18next.t('appcore:redesignedSubjectPicker.noUnitsText')

const noSearchResultsText = (_: SupportedLanguage) => i18next.t('appcore:common.noSearchResultsText')
